import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { usePollingQuery } from "@hooks/usePollingQuery";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { Card, CardContent } from "@shadcn/ui/card";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "@utils/stripe";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { CircleCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { PaymentForm, StatusCard, stripeAppearance } from "~/components/lock/B2CCheckout/B2CSessionCheckout";
import { hasErrorCode } from "~/components/lock/B2CCheckout/outstandingBalance";
import { paymentNeedsCustomer } from "~/components/lock/B2CCheckout/paymentIssue";
import { formatB2CPrice } from "~/components/lock/B2CCheckout/pricing";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { paySessionContinueMutation } from "./__generated__/paySessionContinueMutation.graphql";
import type { paySessionQuery, paySessionQuery$data } from "./__generated__/paySessionQuery.graphql";

const query = graphql`
	query paySessionQuery($sessionId: UUID!) @relay_test_operation {
		b2CLockSession(by: { id: $sessionId }) {
			id
			status
			paymentStatus
			activePayment {
				id
				amount
				stripeStatus
				paymentIntentSecret
			}
		}
	}
`;

const continueMutation = graphql`
	mutation paySessionContinueMutation($paymentId: UUID!) {
		b2CLockSessionPaymentContinue(id: $paymentId) {
			id
			paymentIntentSecret
		}
	}
`;

type Session = NonNullable<paySessionQuery$data["b2CLockSession"]>;

/** The payment of a started or ended rental that Stripe could not complete without the customer. */
function pendingCustomerPayment(session: Session) {
	if (session.status !== "RENTAL_STARTED" && session.status !== "RENTAL_ENDED") return null;
	const payment = session.activePayment;
	return payment && paymentNeedsCustomer(payment.stripeStatus) ? payment : null;
}

/** Nothing is left for the customer to do: paid, or the rental continues on a valid payment. */
function isSettled(session: Session) {
	if (pendingCustomerPayment(session)) return false;
	if (session.status === "RENTAL_STARTED") return session.activePayment?.stripeStatus !== "PROCESSING";
	return session.status === "RENTAL_ENDED" && session.paymentStatus === "COMPLETED";
}

export const Route = createFileRoute("/$hubMetaName/lock/pay/$sessionId")({
	beforeLoad: () => {
		document.title = "bacehub | Lock payment";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		if (!(await checkSession())) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/lock/pay/${ctx.params.sessionId}` },
			});
		}
		return preloadQuery<paySessionQuery>(
			ctx.context.relayEnvironment,
			query,
			{ sessionId: ctx.params.sessionId },
			{ fetchPolicy: "network-only" }
		);
	},
});

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const { hubMetaName, sessionId } = Route.useParams();
	const navigate = useNavigate();
	const { b2CLockSession: initial } = usePreloadedQuery<paySessionQuery>(query, Route.useLoaderData());
	const [session, setSession] = useState<Session | null>(initial ?? null);

	usePollingQuery<paySessionQuery, "settled">({
		query,
		variables: { sessionId },
		intervalMs: 2000,
		sessionKey: sessionId,
		enabled: session !== null && !isSettled(session),
		createEvaluator:
			() =>
			({ b2CLockSession }) =>
				b2CLockSession && isSettled(b2CLockSession) ? { type: "complete", result: "settled" } : { type: "continue" },
		onData: ({ b2CLockSession }) => setSession(b2CLockSession ?? null),
	});

	useEffect(() => {
		if (session?.status === "AWAITING_PAYMENT") {
			void navigate({ to: "/$hubMetaName/lock/new", params: { hubMetaName }, search: { session: session.id }, replace: true });
		}
	}, [hubMetaName, navigate, session]);

	const backToRentals = () => navigate({ to: "/$hubMetaName/lock", params: { hubMetaName } });
	const payment = session ? pendingCustomerPayment(session) : null;

	return (
		<PageContainer className="min-h-dvh bg-slate-50">
			<AppHeader service="LOCK_B2C" />
			<main className="mx-auto flex w-full max-w-[390px] flex-col gap-6 px-6 py-6">
				<h1 className="text-2xl font-breit font-medium leading-8">
					<T>{i18n.LOCK_B2C_SETTLE_TITLE()}</T>
				</h1>
				{!session || session.status === "CANCELLED" ? (
					<Notice text={i18n.LOCK_B2C_SETTLE_UNAVAILABLE()} action={i18n.LOCK_B2C_SETTLE_BACK()} onAction={backToRentals} />
				) : payment ? (
					<PendingPayment payment={payment} returnUrl={`${window.location.origin}${window.location.pathname}`} />
				) : isSettled(session) ? (
					<Notice
						icon
						title={i18n.LOCK_B2C_SETTLE_DONE_TITLE()}
						text={i18n.LOCK_B2C_SETTLE_DONE_DESC()}
						action={i18n.LOCK_B2C_SETTLE_BACK()}
						onAction={backToRentals}
					/>
				) : (
					<StatusCard text={i18n.LOCK_B2C_SETTLE_CONFIRMING()} />
				)}
			</main>
		</PageContainer>
	);
}

function PendingPayment({ payment, returnUrl }: { payment: NonNullable<Session["activePayment"]>; returnUrl: string }) {
	const i18n = useI18nContext().LL["new-ui"];
	const { locale } = useI18nContext();
	const [continuePayment, isContinuing] = useAsyncMutation<paySessionContinueMutation>(continueMutation);
	const [clientSecret, setClientSecret] = useState(payment.paymentIntentSecret ?? null);
	const [preparationFailed, setPreparationFailed] = useState(false);
	const [notOwner, setNotOwner] = useState(false);
	const continueAttempted = useRef(false);
	const amount = formatB2CPrice(payment.amount / 100);

	const preparePayment = async () => {
		setPreparationFailed(false);
		try {
			const response = await continuePayment({ variables: { paymentId: payment.id } });
			const secret = response.b2CLockSessionPaymentContinue.paymentIntentSecret;
			if (secret) setClientSecret(secret);
			else setPreparationFailed(true);
		} catch (error) {
			console.error(error);
			// Admins can read other users' rentals, but only the renter can pay them.
			if (hasErrorCode(error, "UNAUTHORIZED")) setNotOwner(true);
			else setPreparationFailed(true);
		}
	};

	useEffect(() => {
		if (clientSecret || continueAttempted.current) return;
		continueAttempted.current = true;
		void preparePayment();
	}, [clientSecret]);

	return (
		<div className="flex flex-col gap-6">
			<p className="text-sm leading-5 text-muted-foreground">
				<T>{i18n.LOCK_B2C_SETTLE_DESC({ amount })}</T>
			</p>
			<div className="flex justify-between gap-4 rounded-xl bg-zinc-100 px-4 py-3 font-breit font-medium">
				<span>{i18n.LOCK_B2C_SETTLE_AMOUNT()}</span>
				<span>{amount}</span>
			</div>
			{notOwner ? (
				<p className="text-sm text-muted-foreground">{i18n.LOCK_B2C_SETTLE_NOT_OWNER()}</p>
			) : clientSecret ? (
				<Elements stripe={stripePromise} options={{ clientSecret, locale, appearance: stripeAppearance, loader: "auto" }}>
					<PaymentForm returnUrl={returnUrl} authorizedText={i18n.LOCK_B2C_SETTLE_CONFIRMING()} />
				</Elements>
			) : preparationFailed ? (
				<div className="flex flex-col gap-3">
					<p className="text-sm text-destructive">{i18n.LOCK_B2C_SETTLE_PREPARATION_FAILED()}</p>
					<Button type="button" variant="outline" disabled={isContinuing} onClick={() => void preparePayment()}>
						{i18n.LOCK_B2C_SETTLE_RETRY()}
					</Button>
				</div>
			) : (
				<StatusCard text={i18n.LOCK_B2C_PAYMENT_PREPARING()} />
			)}
		</div>
	);
}

function Notice(props: { title?: string; text: string; action: string; onAction: () => void; icon?: boolean }) {
	return (
		<Card>
			<CardContent className="flex flex-col gap-4 pt-6">
				{props.title ? (
					<div className="flex items-center gap-2 font-breit text-lg font-medium">
						{props.icon ? <CircleCheck className="size-5 text-green-600" /> : null}
						{props.title}
					</div>
				) : null}
				<p className="text-sm text-muted-foreground">{props.text}</p>
				<Button onClick={props.onAction}>{props.action}</Button>
			</CardContent>
		</Card>
	);
}
